import React, { useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import PageLayout from "../components/ui/PageLayout";
import LogoSpinner from "../components/ui/LogoSpinner";
import { Panel, StatTile } from "../components/ui/Panel";
import { Progress } from "../components/ui/Progress";
import { EmptyState } from "../components/ui/EmptyState";
import { FadeIn } from "../components/ui/FadeIn";
import { SkeletonGroup, SkeletonText } from "../components/ui/Skeleton";

const Statistics = () => {
  const drones = useFleetStore((state) => state.drones);
  const flags = useFleetStore((state) => state.flags);
  const history = useHistory();
  const api = useApi();

  // Fetch all-time flag counts, maintenance counts, and travel stats
  const [allTimeFlagCounts, setAllTimeFlagCounts] = useState({});
  const [maintenanceCounts, setMaintenanceCounts] = useState({});
  const [travelStats, setTravelStats] = useState({ drones: [], total: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    if (drones.length === 0) return;

    const fetchStats = async () => {
      try {
        // Fetch flags, maintenance, and travel stats in parallel
        const [allFlags, allMaint, travelData] = await Promise.all([
          api.get('/flags'),
          api.get('/maintenance'),
          api.get('/location-history/stats/most-traveled?limit=10').catch(() => ({ drones: [], total: 0 }))
        ]);

        // Only count stats for drones in the active fleet (backend-scoped)
        const fleetSerials = new Set(drones.map(d => d.serialnumber));

        // Count all flag entries per drone, only for active fleet
        const flagCounts = allFlags.reduce((acc, flag) => {
          const serial = flag.drone_serial;
          if (serial && fleetSerials.has(serial)) {
            acc[serial] = (acc[serial] || 0) + 1;
          }
          return acc;
        }, {});
        setAllTimeFlagCounts(flagCounts);

        // Count maintenance entries per drone, only for active fleet
        const maintCounts = allMaint.reduce((acc, log) => {
          const serial = log.drone_serial;
          if (serial && fleetSerials.has(serial)) {
            acc[serial] = (acc[serial] || 0) + 1;
          }
          return acc;
        }, {});
        setMaintenanceCounts(maintCounts);

        // Travel stats already filtered by backend
        setTravelStats(travelData);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [drones]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute all statistics
  const stats = useMemo(() => {
    if (drones.length === 0) return null;

    // Fleet Status Breakdown
    const statusCounts = drones.reduce((acc, drone) => {
      const status = drone.condition || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // FAA Registration Stats
    const now = new Date();
    const faaStats = drones.reduce(
      (acc, drone) => {
        if (!drone.faa_reg) {
          acc.unregistered++;
        } else {
          const expDate = new Date(drone.reg_exp);
          if (expDate < now) {
            acc.expired++;
          } else {
            const monthsUntil =
              (expDate.getFullYear() - now.getFullYear()) * 12 +
              (expDate.getMonth() - now.getMonth());
            if (monthsUntil <= 4) {
              acc.expiresSoon++;
            } else {
              acc.active++;
            }
          }
        }
        return acc;
      },
      { active: 0, expiresSoon: 0, expired: 0, unregistered: 0 }
    );

    // Most Flagged Drones (all-time from flags table)
    const mostFlagged = Object.entries(allTimeFlagCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([serial, count]) => ({ serial, count }));

    // Total all-time flags
    const totalAllTimeFlags = Object.values(allTimeFlagCounts)
      .reduce((sum, count) => sum + count, 0);

    // Most Maintained Drones (all-time from maintenance logs)
    const mostMaintained = Object.entries(maintenanceCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([serial, count]) => ({ serial, count }));

    // Total maintenance entries
    const totalMaintenance = Object.values(maintenanceCounts)
      .reduce((sum, count) => sum + count, 0);

    // Oldest Seen (haven't been seen in the longest time)
    const oldestSeen = drones
      .filter((d) => d.last_seen_time)
      .sort((a, b) => new Date(a.last_seen_time) - new Date(b.last_seen_time))
      .slice(0, 10);

    // Customer Distribution — NULL customer_org_name now bucketed as "Firefly Internal"
    // per the Phase A ownership model.
    const customerCounts = drones.reduce((acc, drone) => {
      const customer = drone.customer_org_name || "Firefly Internal";
      acc[customer] = (acc[customer] || 0) + 1;
      return acc;
    }, {});
    const topCustomers = Object.entries(customerCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    return {
      totalDrones: drones.length,
      statusCounts,
      faaStats,
      mostFlagged,
      mostMaintained,
      mostTraveled: travelStats.drones,
      oldestSeen,
      topCustomers,
      activeFlags: flags.length,
      totalAllTimeFlags,
      totalMaintenance,
      totalLocations: travelStats.total,
    };
  }, [drones, flags, allTimeFlagCounts, maintenanceCounts, travelStats]);

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  // Navigate to drone page
  const goToDrone = (serial) => {
    history.push(`/drone/${serial}`);
  };

  if (!stats) {
    return <LogoSpinner label="Loading statistics" />;
  }

  return (
    <PageLayout kind="workspace" className="pt-4 text-white">
      <h1 className="text-2xl font-bold mb-6">Fleet Statistics</h1>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatTile label="Total Drones" value={stats.totalDrones} />
        <StatTile label="Active Flags" value={stats.activeFlags} tone="warn" />
        <StatTile label="Ready To Fly" value={stats.statusCounts.RTF || 0} tone="good" />
        <StatTile label="Out of Commission" value={stats.statusCounts.OOC || 0} tone="danger" />
      </div>

      {/* Status Breakdown */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Panel title="Fleet Status Breakdown">
          <div className="space-y-2">
            {Object.entries(stats.statusCounts).map(([status, count]) => (
              <StatusBar
                key={status}
                label={status}
                count={count}
                total={stats.totalDrones}
                color={getStatusColor(status)}
              />
            ))}
          </div>
        </Panel>

        <Panel title="FAA Registration Status">
          <div className="space-y-2">
            <StatusBar
              label="Active"
              count={stats.faaStats.active}
              total={stats.totalDrones}
              color="bg-green-500"
            />
            <StatusBar
              label="Expires Soon"
              count={stats.faaStats.expiresSoon}
              total={stats.totalDrones}
              color="bg-yellow-500"
            />
            <StatusBar
              label="Expired"
              count={stats.faaStats.expired}
              total={stats.totalDrones}
              color="bg-red-500"
            />
            <StatusBar
              label="Unregistered"
              count={stats.faaStats.unregistered}
              total={stats.totalDrones}
              color="bg-outline"
            />
          </div>
        </Panel>
      </div>

      {/* Top Lists */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Most Flagged (All-Time) */}
        <Panel title={<span className="flex items-center gap-2"><span className="text-yellow-400">!</span> Most Flagged Drones</span>}>
          <p className="text-sm text-on-surface-variant mb-3">
            Total flags issued: <span className="text-yellow-400 font-medium">{stats.totalAllTimeFlags}</span>
          </p>
          {loadingStats ? (
            <FadeIn key="loading"><SkeletonGroup label="Loading flag history"><SkeletonText lines={5} /></SkeletonGroup></FadeIn>
          ) : stats.mostFlagged.length === 0 ? (
            <FadeIn key="empty"><EmptyState variant="area">No flags recorded</EmptyState></FadeIn>
          ) : (
            <FadeIn key="list">
            <ol className="space-y-2">
              {stats.mostFlagged.map(({ serial, count }, i) => (
                <li key={serial} className="flex items-center justify-between">
                  <button
                    onClick={() => goToDrone(serial)}
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <span className="text-on-surface-variant w-5">{i + 1}.</span>
                    <span className="font-mono text-sm">{serial}</span>
                  </button>
                  <span className="text-yellow-400 font-medium">{count} flags</span>
                </li>
              ))}
            </ol>
            </FadeIn>
          )}
        </Panel>

        {/* Most Maintained (All-Time) */}
        <Panel title={<span className="flex items-center gap-2"><span className="text-blue-400">🔧</span> Most Maintained Drones</span>}>
          <p className="text-sm text-on-surface-variant mb-3">
            Total maintenance logs: <span className="text-blue-400 font-medium">{stats.totalMaintenance}</span>
          </p>
          {loadingStats ? (
            <FadeIn key="loading"><SkeletonGroup label="Loading maintenance history"><SkeletonText lines={5} /></SkeletonGroup></FadeIn>
          ) : stats.mostMaintained.length === 0 ? (
            <FadeIn key="empty"><EmptyState variant="area">No maintenance recorded</EmptyState></FadeIn>
          ) : (
            <FadeIn key="list">
            <ol className="space-y-2">
              {stats.mostMaintained.map(({ serial, count }, i) => (
                <li key={serial} className="flex items-center justify-between">
                  <button
                    onClick={() => goToDrone(serial)}
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <span className="text-on-surface-variant w-5">{i + 1}.</span>
                    <span className="font-mono text-sm">{serial}</span>
                  </button>
                  <span className="text-blue-400 font-medium">{count} logs</span>
                </li>
              ))}
            </ol>
            </FadeIn>
          )}
        </Panel>
      </div>

      {/* Second Row of Lists */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Most Traveled (All-Time) */}
        <Panel title={<span className="flex items-center gap-2"><span className="text-green-400">📍</span> Most Traveled Drones</span>}>
          {loadingStats ? (
            <FadeIn key="loading"><SkeletonGroup label="Loading travel history"><SkeletonText lines={5} /></SkeletonGroup></FadeIn>
          ) : stats.mostTraveled.length === 0 ? (
            <FadeIn key="empty"><EmptyState variant="area">No travel data recorded</EmptyState></FadeIn>
          ) : (
            <FadeIn key="list">
            <ol className="space-y-2">
              {stats.mostTraveled.map(({ serial, count }, i) => (
                <li key={serial} className="flex items-center justify-between">
                  <button
                    onClick={() => goToDrone(serial)}
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <span className="text-on-surface-variant w-5">{i + 1}.</span>
                    <span className="font-mono text-sm">{serial}</span>
                  </button>
                  <span className="text-green-400 font-medium">{count} locations</span>
                </li>
              ))}
            </ol>
            </FadeIn>
          )}
        </Panel>

        {/* Longest Since Seen */}
        <Panel title={<span className="flex items-center gap-2"><span className="text-red-400">?</span> Longest Since Seen</span>}>
          <ol className="space-y-2">
            {stats.oldestSeen.map((drone, i) => (
              <li key={drone.serialnumber} className="flex items-center justify-between text-sm">
                <button
                  onClick={() => goToDrone(drone.serialnumber)}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  <span className="text-on-surface-variant w-5">{i + 1}.</span>
                  <span className="font-mono">{drone.serialnumber}</span>
                </button>
                <span className="text-on-surface-variant text-xs">{formatDate(drone.last_seen_time)}</span>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {/* Customer Distribution */}
      <Panel title="Customer Distribution">
        <div className="space-y-2">
          {stats.topCustomers.map(([customer, count]) => (
            <StatusBar
              key={customer}
              label={customer}
              count={count}
              total={stats.totalDrones}
              color="bg-blue-500"
            />
          ))}
        </div>
      </Panel>
    </PageLayout>
  );
};

// The category colours the old bars carried, as the hex Progress takes. The row names the
// category beside the bar, so colour never stands alone.
const BAR = {
  "bg-green-500": { color: "#22c55e" },
  "bg-yellow-500": { color: "#eab308" },
  "bg-red-500": { color: "#ef4444" },
  "bg-blue-500": { color: "#3b82f6" },
  "bg-outline": { tone: "neutral" },
};

// Status Bar Component
const StatusBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="text-on-surface-variant">
          {count} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <Progress value={count} max={total} label={label} size="lg" {...BAR[color]} />
    </div>
  );
};

// Helper to get color for status
const getStatusColor = (status) => {
  switch (status) {
    case "RTF":
      return "bg-green-500";
    case "Flag":
      return "bg-yellow-500";
    case "OOC":
      return "bg-red-500";
    case "NYC":
      return "bg-blue-500";
    default:
      return "bg-outline";
  }
};

export default Statistics;
